import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  useGetMrDataQuery,
  useUpdateStatusMutation,
  useGetpobdataQuery,
  useUpdatepobstatusMutation,
} from "state/api";
import {
  Box,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Grid,
  IconButton,
  Modal,
  ButtonGroup,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import CloseIcon from "@mui/icons-material/Close";
import SmDailyWiseRecords from "../SmDailyWiseRecords";

const Sm = () => {
  const MgCd = useSelector((state) => state.global.MgCd);

  // Fixed: Removed duplicate refetch property
  const {
    data: appdata,
    isLoading,
    refetch: refetchAppData, // Renamed to avoid conflict
  } = useGetMrDataQuery(MgCd);

  const {
    data: pobdata,
    isLoading: pobisLoading,
    refetch: refetchPob,
  } = useGetpobdataQuery(MgCd);

  const [previewImage, setPreviewImage] = useState(null);
  const [updateStatus] = useUpdateStatusMutation();
  const [data, setData] = useState([]);
  const [pdata, setpdata] = useState([]);
  const [updatepobstatus] = useUpdatepobstatusMutation();
  const [approveAction, setApproveAction] = useState(null);
  const [viewMode, setViewMode] = useState("prescription");
  const [pobTypeFilter, setPobTypeFilter] = useState("Power Brand POB value");
  const [pobProductFilter, setPobProductFilter] = useState(null);

  useEffect(() => {
    if (pobdata) {
      setpdata(pobdata);
    }
  }, [pobdata]);

  useEffect(() => {
    if (appdata) setData(appdata);
  }, [appdata]);

  useEffect(() => {
    if (approveAction) {
      setApproveAction(null);
    }
  }, [approveAction]);

  if (pobisLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  const handleViewToggle = (newView) => {
    setViewMode(newView);
  };

  const handlePobTypeFilter = (pobType) => {
    setPobTypeFilter(pobType);
    setPobProductFilter(null); // Reset product filter when POB type changes
  };

  const handlePobProductFilter = (product) => {
    setPobProductFilter(product);
  };

  const handleToggleStatus = async (item, status) => {
    try {
      const payload = {
        id: item.id,
        MrMgCd: item.MrMgCd,
        MclCode: item.MclCode,
        status,
      };

      const response = await updateStatus(payload).unwrap();

      if (response.success) {
        setData((prevData) => prevData.filter((card) => card.id !== item.id));
        refetchAppData(); // Updated to use renamed refetch
        if (status === 1) {
          setApproveAction(item);
        }
      }
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  const handleTogglepobstatus = async (item, status) => {
    try {
      const payload = {
        id: item.id,
        MrMgCd: item.MrMgCd,
        MclCode: item.MclCode,
        status,
      };

      const response = await updatepobstatus(payload).unwrap();

      if (response.success) {
        setpdata((prevData) => prevData.filter((card) => card.id !== item.id));
        refetchPob();
        if (status === 1) {
          setApproveAction(item);
        }
      }
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  const handlePreviewImage = (pic) => setPreviewImage(pic);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)",
        p: 4,
        pb: 8,
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
          pb: 3,
          borderBottom: "1px solid rgba(59, 130, 246, 0.2)",
        }}
      >
        <Typography
          variant="h3"
          fontWeight={700}
          sx={{
            color: "white",
            textShadow: "0 2px 4px rgba(0,0,0,0.2)",
          }}
        >
          Medical Records
        </Typography>
        <ButtonGroup
          variant="contained"
          sx={{
            "& .MuiButton-root": {
              borderColor: "rgba(59, 130, 246, 0.3)",
              transition: "all 0.3s",
            },
          }}
        >
          <Button
            sx={{
              background:
                viewMode === "prescription"
                  ? "linear-gradient(45deg, #3b82f6 0%, #2563eb 100%)"
                  : "rgba(255, 255, 255, 0.1)",
              color: "white",
              border:
                "1px solid" +
                (viewMode === "prescription"
                  ? " rgba(59, 130, 246, 0.5)"
                  : " rgba(255, 255, 255, 0.2)"),
              py: 1,
              px: 3,
              fontWeight: 600,
              "&:hover": {
                background:
                  viewMode === "prescription"
                    ? "linear-gradient(45deg, #3b82f6 0%, #2563eb 100%)"
                    : "rgba(255, 255, 255, 0.15)",
              },
            }}
            onClick={() => handleViewToggle("prescription")}
          >
            Prescription
          </Button>
          <Button
            sx={{
              background:
                viewMode === "invoice"
                  ? "linear-gradient(45deg, #a855f7 0%, #9333ea 100%)"
                  : "rgba(255, 255, 255, 0.1)",
              color: "white",
              border:
                "1px solid" +
                (viewMode === "invoice"
                  ? " rgba(168, 85, 247, 0.5)"
                  : " rgba(255, 255, 255, 0.2)"),
              py: 1,
              px: 3,
              fontWeight: 600,
              "&:hover": {
                background:
                  viewMode === "invoice"
                    ? "linear-gradient(45deg, #a855f7 0%, #9333ea 100%)"
                    : "rgba(255, 255, 255, 0.15)",
              },
            }}
            onClick={() => handleViewToggle("invoice")}
          >
            POB
          </Button>
          <Button
            sx={{
              background:
                viewMode === "dailywise"
                  ? "linear-gradient(45deg, #10b981 0%, #059669 100%)"
                  : "rgba(255, 255, 255, 0.1)",
              color: "white",
              border:
                "1px solid" +
                (viewMode === "dailywise"
                  ? " rgba(16, 185, 129, 0.5)"
                  : " rgba(255, 255, 255, 0.2)"),
              py: 1,
              px: 3,
              fontWeight: 600,
              "&:hover": {
                background:
                  viewMode === "dailywise"
                    ? "linear-gradient(45deg, #10b981 0%, #059669 100%)"
                    : "rgba(255, 255, 255, 0.15)",
              },
            }}
            onClick={() => handleViewToggle("dailywise")}
          >
            Daily Records
          </Button>
        </ButtonGroup>
      </Box>

      {viewMode === "dailywise" ? (
        <SmDailyWiseRecords />
      ) : viewMode === "prescription" ? (
        <Box>
          {/* Filter Header */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                color: "rgba(255, 255, 255, 0.9)",
                fontWeight: 600,
                mb: 2,
              }}
            >
              Prescription Records
            </Typography>
          </Box>

          {/* Prescription Cards Grid */}
          <Grid container spacing={3} justifyContent="flex-start">
            {data && data.length > 0 ? (
              data.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      backgroundColor: "#1e293b",
                      border: "1px solid rgba(59, 130, 246, 0.2)",
                      borderRadius: 2,
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 12px 30px rgba(59, 130, 246, 0.2)",
                        borderColor: "rgba(59, 130, 246, 0.4)",
                      },
                    }}
                  >
                    {/* Image Section */}
                    <Box sx={{ position: "relative", overflow: "hidden" }}>
                      <CardMedia
                        component="img"
                        height="200"
                        image={item.pic}
                        alt={item.DoctName}
                        onClick={() => handlePreviewImage(item.pic)}
                        sx={{
                          cursor: "pointer",
                          objectFit: "cover",
                          transition: "transform 0.3s",
                          "&:hover": {
                            transform: "scale(1.05)",
                          },
                        }}
                      />
                      <IconButton
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          backgroundColor: "rgba(0, 0, 0, 0.5)",
                          backdropFilter: "blur(10px)",
                          color: "white",
                          transition: "all 0.3s",
                          "&:hover": {
                            backgroundColor: "rgba(59, 130, 246, 0.6)",
                            transform: "scale(1.1)",
                          },
                        }}
                        onClick={() => handlePreviewImage(item.pic)}
                      >
                        <ZoomInIcon />
                      </IconButton>
                    </Box>

                    {/* Content Section */}
                    <CardContent
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        background:
                          "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
                        borderTop: "1px solid rgba(59, 130, 246, 0.2)",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          sx={{
                            color: "#3b82f6",
                            mb: 1,
                          }}
                        >
                          {item.name} ({item.HeadQrt})
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "rgba(255, 255, 255, 0.7)",
                            mb: 0.5,
                          }}
                        >
                          <strong>Doctor:</strong> {item.DoctName}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "rgba(255, 255, 255, 0.7)",
                            mb: 0.5,
                          }}
                        >
                          <strong>Category:</strong> {item.DocCategory}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "rgba(255, 255, 255, 0.7)",
                            mb: 0.5,
                          }}
                        >
                          <strong>MCL Code:</strong> {item.MclCode}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "rgba(255, 255, 255, 0.6)",
                            fontSize: "0.75rem",
                            mt: 1,
                          }}
                        >
                          <strong>Date:</strong>{" "}
                          {new Date(item.SUBDATE).toLocaleDateString()}
                        </Typography>
                      </Box>

                      {/* Action Buttons */}
                      <Box display="flex" gap={1} marginTop={2}>
                        <Button
                          variant="contained"
                          fullWidth
                          size="small"
                          sx={{
                            background:
                              "linear-gradient(45deg, #10b981 0%, #059669 100%)",
                            color: "white",
                            fontWeight: 600,
                            transition: "all 0.3s",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 4px 15px rgba(16, 185, 129, 0.3)",
                            },
                          }}
                          onClick={() => handleToggleStatus(item, 1)}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="contained"
                          fullWidth
                          size="small"
                          sx={{
                            background:
                              "linear-gradient(45deg, #ef4444 0%, #dc2626 100%)",
                            color: "white",
                            fontWeight: 600,
                            transition: "all 0.3s",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 4px 15px rgba(239, 68, 68, 0.3)",
                            },
                          }}
                          onClick={() => handleToggleStatus(item, 2)}
                        >
                          Reject
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            ) : (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 10,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    color: "rgba(255, 255, 255, 0.5)",
                    textAlign: "center",
                  }}
                >
                  Great! No prescription pendings... 😎
                </Typography>
              </Box>
            )}
          </Grid>
        </Box>
      ) : (
        <Box>
          {/* POB Type Filter */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                color: "rgba(255, 255, 255, 0.9)",
                fontWeight: 600,
                mb: 2,
              }}
            >
              Filter by POB Type
            </Typography>
            <Box display="flex" gap={2}>
              <Button
                variant={
                  pobTypeFilter === "Power Brand POB value"
                    ? "contained"
                    : "outlined"
                }
                sx={{
                  background:
                    pobTypeFilter === "Power Brand POB value"
                      ? "linear-gradient(45deg, #3b82f6 0%, #2563eb 100%)"
                      : "transparent",
                  color: "white",
                  borderColor:
                    pobTypeFilter === "Power Brand POB value"
                      ? "rgba(59, 130, 246, 0.5)"
                      : "rgba(255, 255, 255, 0.2)",
                  fontWeight: 600,
                  py: 1,
                  px: 3,
                  transition: "all 0.3s",
                  "&:hover": {
                    borderColor: "rgba(59, 130, 246, 0.5)",
                    backgroundColor:
                      pobTypeFilter === "Power Brand POB value"
                        ? "linear-gradient(45deg, #3b82f6 0%, #2563eb 100%)"
                        : "rgba(59, 130, 246, 0.1)",
                  },
                }}
                onClick={() => handlePobTypeFilter("Power Brand POB value")}
              >
                Power Brand POB
              </Button>
              <Button
                variant={
                  pobTypeFilter === "JP Tone POB value"
                    ? "contained"
                    : "outlined"
                }
                sx={{
                  background:
                    pobTypeFilter === "JP Tone POB value"
                      ? "linear-gradient(45deg, #a855f7 0%, #9333ea 100%)"
                      : "transparent",
                  color: "white",
                  borderColor:
                    pobTypeFilter === "JP Tone POB value"
                      ? "rgba(168, 85, 247, 0.5)"
                      : "rgba(255, 255, 255, 0.2)",
                  fontWeight: 600,
                  py: 1,
                  px: 3,
                  transition: "all 0.3s",
                  "&:hover": {
                    borderColor: "rgba(168, 85, 247, 0.5)",
                    backgroundColor:
                      pobTypeFilter === "JP Tone POB value"
                        ? "linear-gradient(45deg, #a855f7 0%, #9333ea 100%)"
                        : "rgba(168, 85, 247, 0.1)",
                  },
                }}
                onClick={() => handlePobTypeFilter("JP Tone POB value")}
              >
                GROWTH BRAND
              </Button>
            </Box>
          </Box>

          {/* POB Product Filter */}
          {pobTypeFilter && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  color: "rgba(255, 255, 255, 0.9)",
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                Filter by Product
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Button
                  variant={pobProductFilter === null ? "contained" : "outlined"}
                  sx={{
                    background:
                      pobProductFilter === null
                        ? "linear-gradient(45deg, #3b82f6 0%, #2563eb 100%)"
                        : "transparent",
                    color: "white",
                    borderColor:
                      pobProductFilter === null
                        ? "rgba(59, 130, 246, 0.5)"
                        : "rgba(255, 255, 255, 0.2)",
                    fontWeight: 600,
                    py: 1,
                    px: 3,
                    transition: "all 0.3s",
                    "&:hover": {
                      borderColor: "rgba(59, 130, 246, 0.5)",
                      backgroundColor:
                        pobProductFilter === null
                          ? "linear-gradient(45deg, #3b82f6 0%, #2563eb 100%)"
                          : "rgba(59, 130, 246, 0.1)",
                    },
                  }}
                  onClick={() => handlePobProductFilter(null)}
                >
                  All
                </Button>

                {pobTypeFilter === "Power Brand POB value" && (
                  <>
                    <Button
                      variant={
                        pobProductFilter === "KTC" ? "contained" : "outlined"
                      }
                      sx={{
                        background:
                          pobProductFilter === "KTC"
                            ? "linear-gradient(45deg, #3b82f6 0%, #2563eb 100%)"
                            : "transparent",
                        color: "white",
                        borderColor:
                          pobProductFilter === "KTC"
                            ? "rgba(59, 130, 246, 0.5)"
                            : "rgba(255, 255, 255, 0.2)",
                        fontWeight: 600,
                        py: 1,
                        px: 3,
                        transition: "all 0.3s",
                        "&:hover": {
                          borderColor: "rgba(59, 130, 246, 0.5)",
                          backgroundColor:
                            pobProductFilter === "KTC"
                              ? "linear-gradient(45deg, #3b82f6 0%, #2563eb 100%)"
                              : "rgba(59, 130, 246, 0.1)",
                        },
                      }}
                      onClick={() => handlePobProductFilter("KTC")}
                    >
                      KTC
                    </Button>
                    <Button
                      variant={
                        pobProductFilter === "EUKROMA"
                          ? "contained"
                          : "outlined"
                      }
                      sx={{
                        background:
                          pobProductFilter === "EUKROMA"
                            ? "linear-gradient(45deg, #3b82f6 0%, #2563eb 100%)"
                            : "transparent",
                        color: "white",
                        borderColor:
                          pobProductFilter === "EUKROMA"
                            ? "rgba(59, 130, 246, 0.5)"
                            : "rgba(255, 255, 255, 0.2)",
                        fontWeight: 600,
                        py: 1,
                        px: 3,
                        transition: "all 0.3s",
                        "&:hover": {
                          borderColor: "rgba(59, 130, 246, 0.5)",
                          backgroundColor:
                            pobProductFilter === "EUKROMA"
                              ? "linear-gradient(45deg, #3b82f6 0%, #2563eb 100%)"
                              : "rgba(59, 130, 246, 0.1)",
                        },
                      }}
                      onClick={() => handlePobProductFilter("EUKROMA")}
                    >
                      EUKROMA
                    </Button>
                    <Button
                      variant={
                        pobProductFilter === "PRU" ? "contained" : "outlined"
                      }
                      sx={{
                        background:
                          pobProductFilter === "PRU"
                            ? "linear-gradient(45deg, #3b82f6 0%, #2563eb 100%)"
                            : "transparent",
                        color: "white",
                        borderColor:
                          pobProductFilter === "PRU"
                            ? "rgba(59, 130, 246, 0.5)"
                            : "rgba(255, 255, 255, 0.2)",
                        fontWeight: 600,
                        py: 1,
                        px: 3,
                        transition: "all 0.3s",
                        "&:hover": {
                          borderColor: "rgba(59, 130, 246, 0.5)",
                          backgroundColor:
                            pobProductFilter === "PRU"
                              ? "linear-gradient(45deg, #3b82f6 0%, #2563eb 100%)"
                              : "rgba(59, 130, 246, 0.1)",
                        },
                      }}
                      onClick={() => handlePobProductFilter("PRU")}
                    >
                      PRU
                    </Button>
                  </>
                )}

                {pobTypeFilter === "JP Tone POB value" && (
                  <>
                    <Button
                      variant={
                        pobProductFilter === "JP TONE ACTIVE"
                          ? "contained"
                          : "outlined"
                      }
                      sx={{
                        background:
                          pobProductFilter === "JP TONE ACTIVE"
                            ? "linear-gradient(45deg, #a855f7 0%, #9333ea 100%)"
                            : "transparent",
                        color: "white",
                        borderColor:
                          pobProductFilter === "JP TONE ACTIVE"
                            ? "rgba(168, 85, 247, 0.5)"
                            : "rgba(255, 255, 255, 0.2)",
                        fontWeight: 600,
                        py: 1,
                        px: 3,
                        transition: "all 0.3s",
                        "&:hover": {
                          borderColor: "rgba(168, 85, 247, 0.5)",
                          backgroundColor:
                            pobProductFilter === "JP TONE ACTIVE"
                              ? "linear-gradient(45deg, #a855f7 0%, #9333ea 100%)"
                              : "rgba(168, 85, 247, 0.1)",
                        },
                      }}
                      onClick={() => handlePobProductFilter("JP TONE ACTIVE")}
                    >
                      JP TONE ACTIVE
                    </Button>
                    <Button
                      variant={
                        pobProductFilter === "JP TONE JUNIOR"
                          ? "contained"
                          : "outlined"
                      }
                      sx={{
                        background:
                          pobProductFilter === "JP TONE JUNIOR"
                            ? "linear-gradient(45deg, #a855f7 0%, #9333ea 100%)"
                            : "transparent",
                        color: "white",
                        borderColor:
                          pobProductFilter === "JP TONE JUNIOR"
                            ? "rgba(168, 85, 247, 0.5)"
                            : "rgba(255, 255, 255, 0.2)",
                        fontWeight: 600,
                        py: 1,
                        px: 3,
                        transition: "all 0.3s",
                        "&:hover": {
                          borderColor: "rgba(168, 85, 247, 0.5)",
                          backgroundColor:
                            pobProductFilter === "JP TONE JUNIOR"
                              ? "linear-gradient(45deg, #a855f7 0%, #9333ea 100%)"
                              : "rgba(168, 85, 247, 0.1)",
                        },
                      }}
                      onClick={() => handlePobProductFilter("JP TONE JUNIOR")}
                    >
                      JP TONE JUNIOR
                    </Button>
                    <Button
                      variant={
                        pobProductFilter === "VENTOBIL M"
                          ? "contained"
                          : "outlined"
                      }
                      sx={{
                        background:
                          pobProductFilter === "VENTOBIL M"
                            ? "linear-gradient(45deg, #a855f7 0%, #9333ea 100%)"
                            : "transparent",
                        color: "white",
                        borderColor:
                          pobProductFilter === "VENTOBIL M"
                            ? "rgba(168, 85, 247, 0.5)"
                            : "rgba(255, 255, 255, 0.2)",
                        fontWeight: 600,
                        py: 1,
                        px: 3,
                        transition: "all 0.3s",
                        "&:hover": {
                          borderColor: "rgba(168, 85, 247, 0.5)",
                          backgroundColor:
                            pobProductFilter === "VENTOBIL M"
                              ? "linear-gradient(45deg, #a855f7 0%, #9333ea 100%)"
                              : "rgba(168, 85, 247, 0.1)",
                        },
                      }}
                      onClick={() => handlePobProductFilter("VENTOBIL M")}
                    >
                      VENTOBIL M
                    </Button>
                    <Button
                      variant={
                        pobProductFilter === "SUNKROMA ADVANCED"
                          ? "contained"
                          : "outlined"
                      }
                      sx={{
                        background:
                          pobProductFilter === "SUNKROMA ADVANCED"
                            ? "linear-gradient(45deg, #a855f7 0%, #9333ea 100%)"
                            : "transparent",
                        color: "white",
                        borderColor:
                          pobProductFilter === "SUNKROMA ADVANCED"
                            ? "rgba(168, 85, 247, 0.5)"
                            : "rgba(255, 255, 255, 0.2)",
                        fontWeight: 600,
                        py: 1,
                        px: 3,
                        transition: "all 0.3s",
                        "&:hover": {
                          borderColor: "rgba(168, 85, 247, 0.5)",
                          backgroundColor:
                            pobProductFilter === "SUNKROMA ADVANCED"
                              ? "linear-gradient(45deg, #a855f7 0%, #9333ea 100%)"
                              : "rgba(168, 85, 247, 0.1)",
                        },
                      }}
                      onClick={() =>
                        handlePobProductFilter("SUNKROMA ADVANCED")
                      }
                    >
                      SUNKROMA ADVANCED
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          )}

          {/* POB Cards Grid */}
          <Grid container spacing={3} justifyContent="flex-start">
            {pdata &&
            pdata.filter(
              (item) =>
                item.pobType === pobTypeFilter &&
                (pobProductFilter === null ||
                  item.pobProduct === pobProductFilter),
            ).length > 0 ? (
              pdata
                .filter(
                  (item) =>
                    item.pobType === pobTypeFilter &&
                    (pobProductFilter === null ||
                      item.pobProduct === pobProductFilter),
                )
                .map((item, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "#1e293b",
                        border: "1px solid rgba(168, 85, 247, 0.2)",
                        borderRadius: 2,
                        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: "0 12px 30px rgba(168, 85, 247, 0.2)",
                          borderColor: "rgba(168, 85, 247, 0.4)",
                        },
                      }}
                    >
                      {/* Image Section */}
                      <Box sx={{ position: "relative", overflow: "hidden" }}>
                        <CardMedia
                          component="img"
                          height="200"
                          image={item.pic}
                          alt={item.DoctName}
                          onClick={() => handlePreviewImage(item.pic)}
                          sx={{
                            cursor: "pointer",
                            objectFit: "cover",
                            transition: "transform 0.3s",
                            "&:hover": {
                              transform: "scale(1.05)",
                            },
                          }}
                        />
                        <IconButton
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            backdropFilter: "blur(10px)",
                            color: "white",
                            transition: "all 0.3s",
                            "&:hover": {
                              backgroundColor: "rgba(168, 85, 247, 0.6)",
                              transform: "scale(1.1)",
                            },
                          }}
                          onClick={() => handlePreviewImage(item.pic)}
                        >
                          <ZoomInIcon />
                        </IconButton>
                      </Box>

                      {/* Content Section */}
                      <CardContent
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          background:
                            "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
                          borderTop: "1px solid rgba(168, 85, 247, 0.2)",
                        }}
                      >
                        <Box>
                          <Typography
                            variant="h6"
                            fontWeight={700}
                            sx={{
                              color: "#a855f7",
                              mb: 1,
                            }}
                          >
                            {item.name} ({item.HeadQrt})
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "rgba(255, 255, 255, 0.7)",
                              mb: 0.5,
                            }}
                          >
                            <strong>Doctor:</strong> {item.DoctName}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "rgba(255, 255, 255, 0.7)",
                              mb: 0.5,
                            }}
                          >
                            <strong>Category:</strong> {item.DocCategory}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "rgba(255, 255, 255, 0.7)",
                              mb: 0.5,
                            }}
                          >
                            <strong>POB Type:</strong> {item.pobType}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "rgba(255, 255, 255, 0.7)",
                              mb: 0.5,
                            }}
                          >
                            <strong>Product:</strong> {item.pobProduct}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "rgba(255, 255, 255, 0.7)",
                              mb: 0.5,
                              fontSize: "0.85rem",
                            }}
                          >
                            <strong>Strips:</strong> {item.No_of_strips}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "rgba(255, 255, 255, 0.6)",
                              fontSize: "0.75rem",
                              mt: 1,
                            }}
                          >
                            <strong>Date:</strong>{" "}
                            {new Date(item.SUBDATE).toLocaleDateString()}
                          </Typography>
                        </Box>

                        {/* Action Buttons */}
                        <Box display="flex" gap={1} marginTop={2}>
                          <Button
                            variant="contained"
                            fullWidth
                            size="small"
                            sx={{
                              background:
                                "linear-gradient(45deg, #10b981 0%, #059669 100%)",
                              color: "white",
                              fontWeight: 600,
                              transition: "all 0.3s",
                              "&:hover": {
                                transform: "translateY(-2px)",
                                boxShadow: "0 4px 15px rgba(16, 185, 129, 0.3)",
                              },
                            }}
                            onClick={() => handleTogglepobstatus(item, 1)}
                          >
                            Approve
                          </Button>
                          <Button
                            variant="contained"
                            fullWidth
                            size="small"
                            sx={{
                              background:
                                "linear-gradient(45deg, #ef4444 0%, #dc2626 100%)",
                              color: "white",
                              fontWeight: 600,
                              transition: "all 0.3s",
                              "&:hover": {
                                transform: "translateY(-2px)",
                                boxShadow: "0 4px 15px rgba(239, 68, 68, 0.3)",
                              },
                            }}
                            onClick={() => handleTogglepobstatus(item, 2)}
                          >
                            Reject
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))
            ) : (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 10,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    color: "rgba(255, 255, 255, 0.5)",
                    textAlign: "center",
                  }}
                >
                  Great! No invoice pendings for the selected filters... 😎
                </Typography>
              </Box>
            )}
          </Grid>
        </Box>
      )}

      {/* Image Preview Modal */}
      <Modal
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            position: "relative",
            maxWidth: "90vw",
            maxHeight: "90vh",
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            borderRadius: 2,
            backdropFilter: "blur(10px)",
            outline: "none",
          }}
        >
          <IconButton
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "white",
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(10px)",
              transition: "all 0.3s",
              "&:hover": {
                backgroundColor: "rgba(239, 68, 68, 0.6)",
                transform: "scale(1.1)",
              },
              zIndex: 1,
            }}
            onClick={() => setPreviewImage(null)}
          >
            <CloseIcon />
          </IconButton>
          <img
            src={previewImage}
            alt="Preview"
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              display: "block",
              borderRadius: "8px",
            }}
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default Sm;
